import { cn } from "@/lib/utils";
import { useMemo } from "react";
import { ChevronLeft, ChevronRight, ChevronsUpDown, ChevronUp, ChevronDown, Inbox, Download } from "lucide-react";
import { Button } from "./button";
import { compareInDirection } from "@/lib/table-sort";
import { usePersistedSort, type SortDir } from "@/lib/use-persisted-sort";

export interface Column<T> {
    key: string;
    header: string;
    className?: string;
    render?: (row: T) => React.ReactNode;
    /** When true, the header becomes a clickable sort toggle. */
    sortable?: boolean;
    /** Value used for comparison when sorting; falls back to `row[key]`. */
    sortAccessor?: (row: T) => string | number | null | undefined;
    /** Direction of the FIRST click on this header. Money, dates and counts
     *  usually want "desc" — biggest / newest first. Defaults to "asc". */
    firstSortDir?: SortDir;
}

export interface ControlledSort {
    key: string | null;
    dir: SortDir;
    onChange: (key: string, firstDir: SortDir) => void;
}

/** Sorts rows the way the grid does: by the column's `sortAccessor`, else `row[key]`. */
export function sortRowsByColumn<T extends Record<string, any>>(
    rows: T[],
    columns: Column<T>[],
    key: string | null,
    dir: SortDir,
): T[] {
    if (!key) return rows;
    const col = columns.find((c) => c.key === key);
    if (!col) return rows;
    const accessor = col.sortAccessor ?? ((row: T) => row[key]);
    // Array.prototype.sort is stable, so rows tying on the sort column keep
    // the order the server sent them in.
    return [...rows].sort((a, b) => compareInDirection(accessor(a), accessor(b), dir));
}

/**
 * Sort ALL rows before the page cuts them down (a "show 10, then Show more"
 * preview), remembered per user like the grid's own sort. Pass the returned
 * `controlledSort` to every DataTable that shows a slice of `sorted`.
 */
export function useSortedRows<T extends Record<string, any>>(
    rows: T[],
    columns: Column<T>[],
    opts: { sortId?: string; defaultKey?: string | null; defaultDir?: SortDir } = {},
): { sorted: T[]; controlledSort: ControlledSort } {
    const sort = usePersistedSort({
        tableId: opts.sortId,
        defaultKey: opts.defaultKey ?? null,
        defaultDir: opts.defaultDir,
        validKeys: columns.filter((c) => c.sortable).map((c) => c.key),
    });
    const sorted = useMemo(
        () => sortRowsByColumn(rows, columns, sort.sortKey, sort.sortDir),
        [rows, columns, sort.sortKey, sort.sortDir],
    );
    return {
        sorted,
        controlledSort: { key: sort.sortKey, dir: sort.sortDir, onChange: (k, first) => sort.toggleSort(k, first) },
    };
}

/**
 * A trailing "Open ›" cue for a table whose whole row opens the record
 * (`onRowClick`). Quiet chevron at rest; on hover it says Open, so the row
 * reads as one big button rather than a sentence with one tiny link in it.
 */
export function openRowColumn<T>(): Column<T> {
    return {
        key: "__open",
        header: "",
        className: "w-16 text-right",
        render: () => (
            <span className="whitespace-nowrap text-xs font-bold text-foreground/30 group-hover:text-primary">
                <span className="hidden group-hover:inline">Open </span>›
            </span>
        ),
    };
}

interface DataTableProps<T extends Record<string, any>> {
    columns: Column<T>[];
    data: T[];
    keyField: string;
    onRowClick?: (row: T) => void;
    /** Highlight rows on hover even when there's no `onRowClick` (helps line up
     *  per-row action buttons on wide tables). */
    hoverable?: boolean;
    selectedId?: string | number | null;
    loading?: boolean;
    emptyMessage?: string;
    page?: number;
    totalPages?: number;
    onPageChange?: (page: number) => void;
    /** Initial sort column key (matches a `Column.key`). */
    defaultSortKey?: string;
    /** Initial sort direction; defaults to "asc". */
    defaultSortDir?: SortDir;
    /**
     * Names this table so the sort a person clicks is remembered for them
     * until they click a different one (saved per user, server-side). Use a
     * short stable id like "customer.invoices"; omit to forget on reload.
     */
    sortId?: string;
    /**
     * The page owns the order. When given, the grid does NOT reorder `data`
     * — it only lights the header and reports clicks. Two cases need it:
     *  - a server-paged list, where sorting the one page on screen would
     *    show "the biggest of page one" as the biggest of all of them;
     *  - a "show 10, then Show more" preview, which has to sort ALL rows
     *    before cutting to ten (see `useSortedRows`).
     */
    controlledSort?: ControlledSort;

    // --- C2-3 (#1312). Every one of these is OPT-IN, per D5: a page that does
    // not pass them renders exactly what it rendered before, which is why none
    // of the 12+ existing call sites changed. There is one grid, never a second.

    /** Rendered above the table. The grid owns no filter state — the page does,
     *  because only the page knows which of its filters the SERVER applies. */
    filters?: React.ReactNode;

    /** Turn on the checkbox column. Selection is controlled by the page so it
     *  survives paging and refetches. */
    selectable?: boolean;
    selectedIds?: Array<string | number>;
    onSelectionChange?: (ids: Array<string | number>) => void;
    /**
     * Which rows may be picked. Rows that fail it render a DISABLED checkbox
     * — visible and in place, never hidden or filtered away, so "why isn't
     * that one in my batch?" is answered by the row itself (#1345).
     * Omitted, every row is selectable exactly as before.
     */
    rowSelectable?: (row: T) => boolean;
    /** Tooltip on a disabled checkbox — why this row can't be picked. */
    rowUnselectableReason?: (row: T) => string;
    /** Shown in the bar that appears while rows are selected. */
    bulkActions?: React.ReactNode;

    /**
     * Total rows the CURRENT QUERY matches, server-side — not `data.length`.
     * Given this, the grid can say honestly that the header checkbox selected
     * the page rather than everything.
     */
    totalRows?: number;
    /** Only when the page can genuinely select the whole result set. Without
     *  it the grid never offers to, because it cannot deliver it. */
    onSelectAllMatching?: () => void;

    /**
     * Export the QUERY, server-side. Per D8 the grid deliberately has no
     * client-side CSV path: it holds one page, so a dump of `data` would
     * silently export 50 of 3,668 rows and look like it worked.
     */
    onExport?: () => void;
    exportLabel?: string;

    /**
     * Opt-in DOM props for each row — #1441's drag-to-reorder needs `draggable`
     * and the drag handlers to sit on the `<tr>` itself, because that is the
     * element the browser drags. Spread onto the row; a `className` here is
     * merged with the grid's own row classes rather than replacing them.
     *
     * Same D5 rule as everything above: a page that does not pass it renders
     * exactly what it rendered before.
     */
    rowProps?: (row: T) => React.HTMLAttributes<HTMLTableRowElement> | undefined;
}

export function DataTable<T extends Record<string, any>>({
    columns,
    data,
    keyField,
    onRowClick,
    hoverable,
    selectedId,
    loading,
    emptyMessage = "No data found",
    page,
    totalPages,
    onPageChange,
    defaultSortKey,
    defaultSortDir = "asc",
    sortId,
    controlledSort,
    filters,
    selectable,
    selectedIds,
    onSelectionChange,
    rowSelectable,
    rowUnselectableReason,
    bulkActions,
    totalRows,
    onSelectAllMatching,
    onExport,
    exportLabel = "Export",
    rowProps,
}: DataTableProps<T>) {
    const hasSortable = columns.some((c) => c.sortable) || defaultSortKey !== undefined;

    const clientSort = usePersistedSort({
        tableId: controlledSort ? undefined : sortId,
        defaultKey: defaultSortKey ?? null,
        defaultDir: defaultSortDir,
        validKeys: columns.filter((c) => c.sortable).map((c) => c.key),
    });
    const sortKey = controlledSort ? controlledSort.key : clientSort.sortKey;
    const sortDir = controlledSort ? controlledSort.dir : clientSort.sortDir;

    const toggleSort = (key: string) => {
        const firstDir = columns.find((c) => c.key === key)?.firstSortDir ?? "asc";
        if (controlledSort) controlledSort.onChange(key, firstDir);
        else clientSort.toggleSort(key, firstDir);
    };

    // --- Selection (C2-3). Controlled by the page, so it survives paging.
    const selected = useMemo(() => new Set((selectedIds ?? []).map(String)), [selectedIds]);
    // Only rows that may actually be picked. A header checkbox that selected
    // rows the action then refuses would be a selection that lies about
    // itself, which is the failure this grid's scope line exists to avoid.
    const pageIds = useMemo(
        () => data.filter((r) => rowSelectable?.(r) ?? true).map((r) => String(r[keyField])),
        [data, keyField, rowSelectable],
    );
    const pageAllSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));

    const setSelection = (next: Set<string>) => onSelectionChange?.([...next]);

    const toggleRow = (id: string) => {
        const next = new Set(selected);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setSelection(next);
    };

    /** The header checkbox takes THIS PAGE, and nothing else. A checkbox that
     *  reads "all" while taking 50 of 3,668 is the bug that made row selection
     *  the wrong tool for bulk jurisdiction (#1313) in the first place. */
    const togglePage = () => {
        const next = new Set(selected);
        if (pageAllSelected) pageIds.forEach((id) => next.delete(id));
        else pageIds.forEach((id) => next.add(id));
        setSelection(next);
    };

    // Offer "select all N" only when the page said it can actually deliver it,
    // and only when there is more to select than what is on screen.
    const moreThanThisPage = totalRows !== undefined && totalRows > pageIds.length;
    const canSelectAllMatching = Boolean(onSelectAllMatching) && moreThanThisPage && pageAllSelected;

    const colCount = columns.length + (selectable ? 1 : 0);

    // Client-side sort of the `data` array as given. For server-paginated
    // consumers this only reorders the current page, which is acceptable for
    // the (non-server-paginated) admin lists this is used by.
    const sortedData = useMemo(() => {
        if (controlledSort || !hasSortable) return data;
        return sortRowsByColumn(data, columns, sortKey, sortDir);
    }, [data, columns, hasSortable, sortKey, sortDir, controlledSort]);

    return (
        <div className="space-y-3">
            {(filters || onExport) && (
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">{filters}</div>
                    {onExport && (
                        <Button variant="outline" size="sm" onClick={onExport}>
                            <Download className="h-4 w-4" />
                            {exportLabel}
                            {/* Names the real number so nobody assumes it is
                                exporting the page they can see. D8. */}
                            {totalRows !== undefined && ` (${totalRows.toLocaleString()} rows)`}
                        </Button>
                    )}
                </div>
            )}

            {selectable && selected.size > 0 && (
                <div className="flex flex-wrap items-center gap-3 rounded-lg border border-primary/30 bg-primary/[0.06] px-3 py-2">
                    <span className="text-sm font-medium tabular-nums">{selected.size.toLocaleString()} selected</span>
                    {/* The honest bit: say that the header checkbox took the
                        page, and only offer the whole set when the page can
                        actually deliver it. */}
                    {canSelectAllMatching && (
                        <button
                            type="button"
                            className="text-sm underline underline-offset-2 hover:no-underline"
                            onClick={onSelectAllMatching}
                        >
                            Select all {totalRows?.toLocaleString()} matching
                        </button>
                    )}
                    {!canSelectAllMatching && moreThanThisPage && pageAllSelected && (
                        <span className="text-xs text-foreground/70">
                            everything on this page — other pages are not included
                        </span>
                    )}
                    <span className="flex-1" />
                    <div className="flex flex-wrap items-center gap-2">{bulkActions}</div>
                    <Button variant="ghost" size="sm" onClick={() => setSelection(new Set())}>
                        Clear
                    </Button>
                </div>
            )}

            <div className="rounded-xl border border-border/70 overflow-hidden shadow-[var(--shadow-card)] bg-card">
                {/* max-md:[&_tbody_a]:* gives row action links a 44px touch target
                    on phones (matches the top-bar tap floor) without bloating
                    desktop row height. */}
                <table className="w-full text-sm max-md:[&_tbody_a]:inline-flex max-md:[&_tbody_a]:min-h-11 max-md:[&_tbody_a]:items-center">
                    <thead>
                        <tr className="border-b border-border bg-muted/85">
                            {selectable && (
                                <th className="h-10 w-10 px-4 text-left">
                                    <input
                                        type="checkbox"
                                        className="h-4 w-4 cursor-pointer align-middle"
                                        checked={pageAllSelected}
                                        onChange={togglePage}
                                        aria-label={
                                            pageAllSelected
                                                ? "Clear selection on this page"
                                                : "Select every row on this page"
                                        }
                                    />
                                </th>
                            )}
                            {columns.map((col) => {
                                const isSorted = sortKey === col.key;
                                return (
                                    <th
                                        key={col.key}
                                        aria-sort={
                                            col.sortable && isSorted
                                                ? sortDir === "asc"
                                                    ? "ascending"
                                                    : "descending"
                                                : undefined
                                        }
                                        className={cn(
                                            "h-10 px-4 text-left text-xs font-bold uppercase tracking-wide text-foreground",
                                            col.className,
                                        )}
                                    >
                                        {col.sortable ? (
                                            <button
                                                type="button"
                                                onClick={() => toggleSort(col.key)}
                                                title={`Sort by ${col.header || "this column"}`}
                                                className={cn(
                                                    "inline-flex items-center gap-1 font-bold uppercase tracking-wide hover:text-foreground/70 max-md:min-h-11",
                                                    // The column the list is ordered by reads at a
                                                    // glance, not only by its small chevron.
                                                    isSorted && "text-primary",
                                                )}
                                            >
                                                {col.header}
                                                {isSorted ? (
                                                    sortDir === "asc" ? (
                                                        <ChevronUp className="h-3.5 w-3.5" />
                                                    ) : (
                                                        <ChevronDown className="h-3.5 w-3.5" />
                                                    )
                                                ) : (
                                                    <ChevronsUpDown className="h-3.5 w-3.5 opacity-40" />
                                                )}
                                            </button>
                                        ) : (
                                            col.header
                                        )}
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {loading ? (
                            <tr>
                                <td colSpan={colCount} className="h-32 text-center">
                                    <div className="flex flex-col items-center gap-2 text-foreground/80">
                                        <div className="h-5 w-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                        <span className="text-sm">Loading...</span>
                                    </div>
                                </td>
                            </tr>
                        ) : sortedData.length === 0 ? (
                            <tr>
                                <td colSpan={colCount} className="h-32 text-center">
                                    <div className="flex flex-col items-center gap-2 text-foreground/80">
                                        <Inbox className="h-8 w-8 opacity-40" />
                                        <span className="text-sm">{emptyMessage}</span>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            sortedData.map((row) => {
                                const { className: extraRowClass, ...extraRowProps } = rowProps?.(row) ?? {};
                                return (
                                    <tr
                                        key={String(row[keyField])}
                                        {...extraRowProps}
                                        onClick={() => onRowClick?.(row)}
                                        className={cn(
                                            "transition-colors duration-100",
                                            // `group` lets a cell react to the row's hover
                                            // (see `openRowColumn`).
                                            onRowClick && "group cursor-pointer",
                                            (onRowClick || hoverable) && "hover:bg-primary/[0.05]",
                                            selectedId !== undefined &&
                                                String(row[keyField]) === String(selectedId) &&
                                                "bg-primary/[0.06] hover:bg-primary/[0.08]",
                                            extraRowClass,
                                        )}
                                    >
                                        {selectable &&
                                            (() => {
                                                const canPick = rowSelectable?.(row) ?? true;
                                                const why = canPick ? undefined : rowUnselectableReason?.(row);
                                                return (
                                                    <td
                                                        className="px-4 py-3 w-10"
                                                        onClick={(e) => e.stopPropagation()}
                                                        title={why}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            className={cn(
                                                                "h-4 w-4 align-middle",
                                                                canPick
                                                                    ? "cursor-pointer"
                                                                    : "cursor-not-allowed opacity-40",
                                                            )}
                                                            disabled={!canPick}
                                                            checked={canPick && selected.has(String(row[keyField]))}
                                                            onChange={() => canPick && toggleRow(String(row[keyField]))}
                                                            aria-label={
                                                                canPick
                                                                    ? `Select row ${String(row[keyField])}`
                                                                    : why || "This row can't be selected"
                                                            }
                                                        />
                                                    </td>
                                                );
                                            })()}
                                        {columns.map((col) => (
                                            <td key={col.key} className={cn("px-4 py-3", col.className)}>
                                                {col.render ? col.render(row) : String(row[col.key] ?? "")}
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
            {totalPages !== undefined && totalPages > 1 && page !== undefined && onPageChange && (
                <div className="flex items-center justify-between px-1">
                    <span className="text-xs font-medium text-foreground/80 tabular-nums">
                        Page {page} of {totalPages}
                    </span>
                    <div className="flex gap-1">
                        <Button
                            variant="outline"
                            size="icon"
                            disabled={page <= 1}
                            onClick={() => onPageChange(page - 1)}
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <Button
                            variant="outline"
                            size="icon"
                            disabled={page >= totalPages}
                            onClick={() => onPageChange(page + 1)}
                        >
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
